import classNames from 'classnames'
import uniqueId from 'lodash/uniqueId'
import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  Fragment,
} from 'react'
import type { ChangeEvent } from 'react'
// @note This `import` has to use a subpath to avoid an import loop:
//   principles (`Features`) -> brown-dwarf (`LegalTakeover`) -> lapidary
import { Size, Theme } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import {
  flattenCharacterPatterns,
  humanizeCharacterPatterns,
  passwordrulesAttribute,
} from '@opallabs/principles/src/PasswordRules'
import { Button, ButtonSize } from '../Button'
import { Icon } from '../Icon'
import { ErrorMessage } from './errorMessage'
import * as styles from './styles'
import { TextInputType } from './types'
import type { TextInputProps, TextInputValidationResult } from './types'
import { useValidityState } from './useValidityState'

export {
  classes as textInputSelectors,
  states as textInputStyleStates,
} from './styles'
export type { TextInputProps, TextInputValidationResult }
export { TextInputType }

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface InputHTMLAttributes<T> {
    /**
     * Give password managers hints for generating passwords.
     *
     * @note This is a non-standard attribute from Apple. Mozilla considers the
     *   attribute to be harmful, and will not implement it.
     *
     * @see https://developer.apple.com/password-rules/
     * @see https://developer.apple.com/documentation/security/password_autofill/customizing_password_autofill_rules
     */
    passwordrules?: string
  }
}

/**
 * Managed text `<input>` with optional validation.
 *
 * This component uses the constraint validation API to improve accessibility.
 * Constraints like `minLength` and `pattern` are automatically applied, but
 * you can also specify a custom `validator` for validations that can't be
 * represented with the standard constraints. If your `validator` indicates the
 * input is invalid this will override the standard constraints; if your
 * `validator` indicates the input is valid the standard constraints will still
 * be applied as a fallback.
 *
 * @note If you provide a custom `validator`, returning `valid: false` will not visually indicate the field has an error
 * unless you also return an `errorMessage`.
 * @note This component doesn’t have a container element; instead it returns up to three top-level sibling elements.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Constraint_validation
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (props, externalRef) => {
    const {
      additionalAriaLabels = {},
      autocompleteHint,
      className,
      clearable = false,
      controlsHtmlId,
      disabled = false,
      enterKeyHint,
      explicitError = '',
      forceValidation = false,
      helpText,
      htmlId = uniqueId('lapidary-text-input'),
      initialValue,
      leadingIconGlyph,
      maxLength,
      maxValue,
      minLength,
      minValue,
      onChange,
      onClick,
      onInput,
      onInputBlur,
      onInputFocus,
      onKeyDown,
      onContainerMouseOut,
      onContainerMouseOver,
      onValidate,
      passwordCharacterPatterns,
      placeholder,
      previouslyModified = false,
      required = false,
      styleOverrides = {},
      theme: propTheme,
      trailingIconGlyph,
      trailingNode,
      type = TextInputType.Text,
      value: valueFromProps,
      validator,
    } = props

    const providedTheme = useTheme()
    const theme = propTheme ?? providedTheme

    const isControlled = valueFromProps !== undefined
    const hasInitialValue = initialValue !== undefined

    const internalRef = useRef<HTMLInputElement | null>(null)
    const clearButtonRef = useRef<HTMLButtonElement | null>(null)
    const [isFocused, setIsFocused] = useState(false)
    const [internalValue, setInternalValue] = useState(
      hasInitialValue ? initialValue : ''
    )
    const value = isControlled ? valueFromProps : internalValue

    let pattern: RegExp | undefined
    let passwordrules: string | undefined
    let title: string | undefined
    if (passwordCharacterPatterns) {
      pattern = flattenCharacterPatterns(passwordCharacterPatterns)
      passwordrules = passwordrulesAttribute(passwordCharacterPatterns)
      title = `Password must include ${humanizeCharacterPatterns(
        passwordCharacterPatterns
      )}`
    }

    const {
      errorMessage,
      modified,
      setCustomErrorMessage,
      setModified,
      setValid,
    } = useValidityState({
      explicitError,
      onValidate,
      patternExplanation: title,
      previouslyModified,
      ref: internalRef,
    })

    // If the input was not initially controlled, reset the internal value when it becomes controlled
    useEffect(() => {
      if (isControlled && internalValue.length) {
        setInternalValue('')
      }
    }, [isControlled, internalValue])

    const handleChange = useCallback(
      (
        event: ChangeEvent<HTMLInputElement> | null,
        manualValue: typeof value | null = ''
      ) => {
        const newValue = (event ? event.target.value : manualValue) || ''

        onChange(newValue)
        setModified(true)
        if (!isControlled) {
          setInternalValue(newValue)
        }

        let result: TextInputValidationResult = {
          valid: true,
        }

        // If there's a custom `validator`, apply its output to `ValidityState`'s
        // 'custom validity'. This has to be done outside `useValidityState` in
        // order to update the validity state before doing anything that depends on
        // the validity state.
        if (validator) {
          result = validator(newValue)
          setCustomErrorMessage((!result.valid && result.errorMessage) || '')
        }

        // Update the `ValidityState`...
        const newValid = internalRef.current?.checkValidity()
        // ... and trigger the `useEffect` that manages `errorMessage`.
        if (typeof newValid !== 'undefined') setValid(newValid)
      },
      [
        isControlled,
        onChange,
        setCustomErrorMessage,
        setModified,
        setValid,
        validator,
      ]
    )

    // Force focus to button in cases we're managing focus in other ways
    // i.e. managed focus in Radix components like Dropdown SubContent
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Tab' && value && clearable) {
        setTimeout(() => clearButtonRef.current?.focus(), 0)
      }

      onKeyDown?.(e)
    }

    const resetSearchValue = () => {
      internalRef.current?.focus()
      handleChange(null, '')
    }

    useEffect(() => {
      if (!forceValidation) return

      handleChange(null, value)
    }, [forceValidation, handleChange, value])

    // Handle `previouslyModified` updates as a 'gentler' alternative to `forceValidation`
    useEffect(() => {
      if (!previouslyModified || !validator) return

      const result = validator(value)
      setCustomErrorMessage((!result.valid && result.errorMessage) || '')

      const newValid = internalRef.current?.checkValidity()
      if (typeof newValid !== 'undefined') setValid(newValid)
    }, [previouslyModified, validator, value, setCustomErrorMessage, setValid])

    /**
     * Use the first non-empty string, if one is available.
     *
     * @note This uses `(error || '')` to avoid selecting `undefined`:
     *   ```
     *   ['', undefined, 'a'].filter(error => error !== '') //=> [undefined, 'a']
     *   ```
     */
    const displayedErrorMessage = [explicitError, errorMessage].find(
      (error: string): boolean => (error || '') !== ''
    )

    return (
      <Fragment>
        <div
          className={classNames(
            'lapidary-text-input-container',
            styles.classes.container,
            {
              [styles.states.hasValue]: Boolean(value),
              [styles.states.disabled]: disabled,
            }
          )}
          css={[
            styles.container({
              theme: theme as Theme,
              modified,
              invalid:
                typeof displayedErrorMessage === 'string' &&
                displayedErrorMessage.length > 0,
              active: isFocused,
            }),
            styleOverrides.container,
          ]}
          onMouseOver={onContainerMouseOver}
          onMouseOut={onContainerMouseOut}
          // Focus and Blur here are intended to bubble up from
          // the child <input /> element, this container should NOT
          // have a tabindex set or be manually focusable by the user
          onFocus={onInputFocus}
          onBlur={onInputBlur}
        >
          {leadingIconGlyph && (
            <Icon
              ariaHidden
              glyph={leadingIconGlyph}
              height={Size.Icon}
              styleOverrides={{
                container: styles.leadingIconContainer,
                icon: styles.leadingIcon({
                  active: Boolean(isFocused || value),
                  theme,
                  disabled,
                }),
              }}
              width={Size.Icon}
            />
          )}
          <input
            aria-controls={controlsHtmlId}
            {...additionalAriaLabels}
            autoComplete={autocompleteHint}
            // @hack 'styled' to override global styles
            className={classNames(
              'styled',
              styles.classes.input,
              'lapidary-text-input',
              className
            )}
            css={[
              styles.input({
                theme: theme as Theme,
              }),
              styleOverrides.input,
            ].flat()}
            disabled={disabled}
            enterKeyHint={enterKeyHint}
            id={htmlId}
            max={maxValue}
            maxLength={maxLength}
            min={minValue}
            minLength={minLength}
            onChange={handleChange}
            onClick={onClick}
            onInput={onInput}
            onKeyDown={handleKeyDown}
            onBlur={() => setIsFocused(false)}
            onFocus={() => setIsFocused(true)}
            passwordrules={passwordrules}
            pattern={pattern?.source}
            placeholder={placeholder}
            ref={(node): void => {
              // @note See https://stackoverflow.com/a/62238917 for an explanation
              //   of `externalRef` and `internalRef`. (Referred to as `ref` and
              //   `myRef`.)
              internalRef.current = node
              if (typeof externalRef === 'function') {
                externalRef(node)
              } else if (externalRef) {
                externalRef.current = node
              }
            }}
            required={required}
            title={title}
            type={type}
            value={value}
          />
          {maxLength && (
            <span
              css={styles.characterCounter({
                theme: theme as Theme,
              })}
            >
              {maxLength - (value?.length ? value?.length : 0)}
            </span>
          )}
          {trailingNode}
          {trailingIconGlyph && (
            <div css={styles.trailingIconContainer}>
              <Icon
                ariaHidden
                glyph={trailingIconGlyph}
                height={Size.Icon}
                styleOverrides={{
                  container: styles.leadingIconContainer,
                  icon: styles.trailingIcon({
                    theme,
                    disabled,
                  }),
                }}
                width={Size.Icon}
              />
            </div>
          )}
          {clearable && (
            <span className="clear-icon-container">
              {Boolean(value) && (
                <Button
                  ariaLabel="Clear Input"
                  variant="ghost"
                  ref={clearButtonRef}
                  css={styles.clearableButton({ theme })}
                  icon={
                    <Icon glyph="gem-close" ariaHidden height={10} width={10} />
                  }
                  name="Clear Input"
                  onFocus={(e) => e.stopPropagation()}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && value && resetSearchValue()
                  }
                  onClick={resetSearchValue}
                  size={ButtonSize.Small}
                />
              )}
            </span>
          )}
        </div>
        <ErrorMessage
          message={displayedErrorMessage}
          theme={theme}
          styleOverrides={styleOverrides.message}
        />
        {helpText && !displayedErrorMessage && (
          <span css={styles.helpText(theme)}>{helpText}</span>
        )}
      </Fragment>
    )
  }
)
TextInput.displayName = 'Lapidary: TextInput'
